<template>
  <div>
    <a-table
      row-key="_id"
      :loading="loading"
      :columns="computedColumns"
      :data-source="invoices"
      :pagination="pagination"
      :scroll="{ x: 'max-content' }"
      :custom-row="onRow"
      :row-class-name="rowClassName"
      @change="(pag) => $emit('change', pag)"
    >
      <template slot="label" slot-scope="text">
        <span class="cell-ellipsis client-name">{{ text || "—" }}</span>
      </template>
      <template slot="invoiceRef" slot-scope="text, record">
        <span class="invoice-cell">{{ record.invoiceNumber || record.reference || "—" }}</span>
      </template>
      <template slot="date" slot-scope="text">
        <span class="date-cell">{{ formatDate(text) }}</span>
      </template>
      <template slot="providerName" slot-scope="text, record">
        <span class="client-name">{{ record._providerName || "—" }}</span>
      </template>
      <template slot="totalAmount" slot-scope="text, record">
        <span class="amount-cell">{{ record._totalAmount != null ? record._totalAmount.toFixed(3) : "—" }}</span>
      </template>
      <template slot="totalPaid" slot-scope="text, record">
        <span class="amount-cell">{{ record._totalPaid != null ? record._totalPaid.toFixed(3) : "—" }}</span>
      </template>
      <template slot="remaining" slot-scope="text, record">
        <span class="amount-cell">{{ record._remaining != null ? record._remaining.toFixed(3) : "—" }}</span>
      </template>
      <template slot="invoiceStatus" slot-scope="text">
        <a-tag :color="invoiceStatusColor(text)">{{ invoiceStatusLabel(text) }}</a-tag>
      </template>
      <template slot="schedulingStatus" slot-scope="text">
        <a-tag :color="text === 'scheduled' ? 'green' : 'orange'">
          {{ text === "scheduled" ? $t("fournisseur.scheduled") : $t("fournisseur.notScheduled") }}
        </a-tag>
      </template>
      <template slot="schedulingProgress" slot-scope="text, record">
        <div class="schedule-progress-cell">
          <span class="schedule-progress-main">
            {{ formatSchedulingProgress(record) }}
            <span v-if="formatNextDueEta(record)" class="text-muted">
              ({{ formatNextDueEta(record) }})
            </span>
          </span>
        </div>
      </template>
      <template slot="action" slot-scope="text, record">
        <a-button type="link" size="small" @click.stop="$emit('row-click', record)">
          <a-icon type="eye" />
        </a-button>
      </template>
    </a-table>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'InvoiceTable',
  props: {
    invoices: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    pagination: { type: [Object, Boolean], default: () => ({ pageSize: 15 }) },
    showLabel: { type: Boolean, default: true },
    showProvider: { type: Boolean, default: false },
    showAction: { type: Boolean, default: true },
  },
  computed: {
    computedColumns() {
      const cols = []
      if (this.showLabel) {
        cols.push({
          title: this.$t('factures.tableLabel'),
          dataIndex: 'label',
          key: 'label',
          ellipsis: true,
          scopedSlots: { customRender: 'label' },
        })
      }
      cols.push(
        {
          title: this.$t('decaissement.invoiceNumber'),
          key: 'invoiceRef',
          scopedSlots: { customRender: 'invoiceRef' },
          width: 130,
        },
        {
          title: this.$t('decaissement.invoiceDate'),
          dataIndex: 'date',
          key: 'date',
          scopedSlots: { customRender: 'date' },
          width: 120,
        },
      )
      if (this.showProvider) {
        cols.push({
          title: this.$t('fournisseur.companyName'),
          key: 'providerName',
          ellipsis: true,
          scopedSlots: { customRender: 'providerName' },
        })
      }
      cols.push(
        {
          title: this.$t('fournisseur.totalAmount'),
          key: 'totalAmount',
          scopedSlots: { customRender: 'totalAmount' },
        },
        {
          title: this.$t('fournisseur.totalPaid'),
          key: 'totalPaid',
          scopedSlots: { customRender: 'totalPaid' },
        },
        {
          title: this.$t('fournisseur.remaining'),
          key: 'remaining',
          scopedSlots: { customRender: 'remaining' },
        },
        {
          title: this.$t('fournisseur.invoiceStatus'),
          dataIndex: 'invoiceStatus',
          key: 'invoiceStatus',
          scopedSlots: { customRender: 'invoiceStatus' },
          width: 120,
        },
        {
          title: this.$t('fournisseur.schedulingStatus'),
          dataIndex: 'schedulingStatus',
          key: 'schedulingStatus',
          scopedSlots: { customRender: 'schedulingStatus' },
          width: 130,
        },
        {
          title: this.$t('fournisseur.scheduleProgress'),
          key: 'schedulingProgress',
          scopedSlots: { customRender: 'schedulingProgress' },
          width: 180,
        },
      )
      if (this.showAction) {
        cols.push({
          title: this.$t('action.actions'),
          key: 'action',
          fixed: 'right',
          width: 72,
          scopedSlots: { customRender: 'action' },
        })
      }
      return cols
    },
  },
  methods: {
    rowClassName(record, index) {
      return index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
    },
    onRow(record) {
      return { on: { click: () => this.$emit('row-click', record) } }
    },
    formatDate(val) {
      return val ? moment(val).format('DD/MM/YYYY') : '—'
    },
    invoiceStatusColor(status) {
      if (status === 'paid') return 'green'
      if (status === 'partial') return 'orange'
      return 'red'
    },
    invoiceStatusLabel(status) {
      if (status === 'paid') return this.$t('fournisseur.paid')
      if (status === 'partial') return this.$t('fournisseur.partial')
      return this.$t('fournisseur.unpaid')
    },
    formatSchedulingProgress(record) {
      const stats = record && record._scheduleStats ? record._scheduleStats : {}
      const paid = Number(stats.paid) || 0
      const total = Number(stats.total) || 0
      if (!total) return '0/0'
      return `${paid}/${total}`
    },
    formatNextDueEta(record) {
      const stats = record && record._scheduleStats ? record._scheduleStats : {}
      if (!stats.nextPendingDueDate) return ''
      const now = moment().startOf('day')
      const due = moment(stats.nextPendingDueDate).startOf('day')
      const days = due.diff(now, 'days')
      if (days < 0) return this.$t('fournisseur.scheduleOverdueByDays', { n: Math.abs(days) })
      if (days === 0) return this.$t('fournisseur.scheduleDueToday')
      if (days < 30) return this.$t('fournisseur.scheduleDueInDays', { n: days })
      const months = due.diff(now, 'months')
      return this.$t('fournisseur.scheduleDueInMonths', { n: Math.max(1, months) })
    },
  },
}
</script>

<style scoped>
.cell-ellipsis {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
.ant-table-tbody tr {
  cursor: pointer;
}
.schedule-progress-cell {
  line-height: 1.2;
}
.schedule-progress-main {
  font-weight: 600;
}
</style>
